import React, { useLayoutEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { makeStyles } from '@material-ui/core/styles';
import { IconButton, Tooltip } from '@material-ui/core';
import ChevronLeftIcon from '@material-ui/icons/ChevronLeft';
import ChevronRightIcon from '@material-ui/icons/ChevronRight';

import { useLocalStorage } from '../../../common/utils';

/**
 * @constant COLLAPSED_ATTRIBUTE
 * @description `data-` attribute this toggle sets on the column that contains it. A global rule in
 * the root theme hides that column's cards while it reads `true`, leaving the toggle itself
 * visible (it opts out through `data-panels-toggle`). Driving the column from an attribute is what
 * keeps this a single self-contained insertion per column: the alternative was threading open/closed
 * state and a wrapper element through all eight result pages.
 * @memberof module:SHARED
 */
const COLLAPSED_ATTRIBUTE = 'data-panels-collapsed';

/**
 * @function useStyles
 * @description Hook for styling using Material-UI's makeStyles. Defines the frosted circular
 * button that collapses an overlay column. It sits over the map, so it needs its own backdrop to
 * stay legible against imagery as well as against the roadmap layer.
 * @param {Object} theme - Material-UI theme containing color, spacing, and other configurations
 * @memberof module:SHARED
 * @returns {Object} Object containing compiled CSS classes for component usage
 */
const useStyles = makeStyles((theme) => ({
  /**
   * `alignSelf` keeps the button off the cross axis of a flex column, but most of the columns this
   * lands in are not flex: EUSCoast's and CSqueeze's are `display: grid`, where `align-self` works
   * the block axis instead and the horizontal one is left to `justify-items`, which those columns
   * set to `stretch` so their cards share one width. The button was pulled to the column's full
   * width along with them - a round icon rendered as a bar as wide as the whole sidebar.
   * `justifySelf` opts it out where the column is a grid, and `width` settles it in either layout:
   * an item with a fit-content width is not stretched, whichever of the two the column turns out
   * to be.
   */
  button: {
    alignSelf: 'flex-start',
    justifySelf: 'start',
    width: 'fit-content',
    flexShrink: 0,
    padding: theme.spacing(0.5),
    color: theme.palette.text.primary,
    backgroundColor:
      theme.palette.type === 'dark' ? 'rgba(18, 43, 61, 0.55)' : 'rgba(255, 255, 255, 0.55)',
    backdropFilter: 'blur(6px)',
    WebkitBackdropFilter: 'blur(6px)',
    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.18)',
    '&:hover': {
      backgroundColor:
        theme.palette.type === 'dark' ? 'rgba(18, 43, 61, 0.8)' : 'rgba(255, 255, 255, 0.8)',
    },
  },
  end: {
    alignSelf: 'flex-end',
    justifySelf: 'end',
    marginLeft: 'auto',
  },
}));

/**
 * @function OverlayPanelsToggle
 * @description Collapses every card in the overlay column it is rendered in, as one action rather
 * than panel by panel, so the map underneath can be read without closing each accordion. Render it
 * as the first child of a column; it marks its parent through {@link COLLAPSED_ATTRIBUTE} and the
 * root theme's global rule hides that column's other children.
 * The arrow points the way the panels will go - outwards to collapse, back inwards to restore -
 * which is why it depends on which side the column is on.
 * The open/closed choice is remembered per side in `localStorage`, shared by every module, so a
 * user who works with the panels hidden does not have to hide them again on each screen.
 * @param {Object} props
 * @param {'left'|'right'} [props.side='left'] - Which column this toggle belongs to.
 * **Called by:
 * @see {ProcessingPage} - each module result page, once per overlay column.
 * @memberof module:SHARED
 * @returns {JSX.Element}
 */
const OverlayPanelsToggle = ({ side = 'left' }) => {
  const classes = useStyles();
  const [t] = useTranslation();
  const ref = useRef(null);
  const [collapsed, setCollapsed] = useLocalStorage(`overlayPanelsCollapsed:${side}`, false);

  useLayoutEffect(() => {
    const column = ref.current?.parentElement;
    if (!column) return;
    column.setAttribute(COLLAPSED_ATTRIBUTE, collapsed ? 'true' : 'false');
  }, [collapsed]);

  const pointsLeft = side === 'left' ? !collapsed : collapsed;
  const label = collapsed ? t('forms.core.overlayPanels.show') : t('forms.core.overlayPanels.hide');

  return (
    <Tooltip title={label}>
      <IconButton
        ref={ref}
        size="small"
        data-panels-toggle="true"
        aria-label={label}
        className={[classes.button, side === 'right' && classes.end].filter(Boolean).join(' ')}
        onClick={() => setCollapsed((previous) => !previous)}>
        {pointsLeft ? <ChevronLeftIcon fontSize="small" /> : <ChevronRightIcon fontSize="small" />}
      </IconButton>
    </Tooltip>
  );
};

export default OverlayPanelsToggle;
